import React, { useState, useEffect, useRef } from 'react';
import { getJobComments, addJobComment, deleteJobComment, removeSelfFromJob, getTeamMembers, getJobOpenItems } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiCamera, FiX, FiImage, FiTrash2, FiDownload, FiAlertCircle, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

function JobComments({ job, onJobUpdated }) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Photo state
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotos, setShowPhotos] = useState(true);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Checklist items still unchecked across every visit to this job.
  const [openItems, setOpenItems] = useState([]);
  const [openItemsInProgress, setOpenItemsInProgress] = useState(0);
  const [showOpenItems, setShowOpenItems] = useState(true);

  const isAssigned = job?.team_members?.some(tm => tm.id === user?.id);
  // Matches can_manage_payroll() on the backend — keep the two in sync.
  const isAdmin = ['Owner', 'Admin', 'Office Manager'].includes(user?.role);

  useEffect(() => {
    if (job?.id) {
      loadComments();
      loadPhotos();
      loadOpenItems();
    }
    loadTeamMembers();
  }, [job?.id]);

  const loadComments = async () => {
    try {
      const response = await getJobComments(job.id);
      setComments(response.data?.comments || response.data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      setComments([]);
    }
  };

  const loadPhotos = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/jobs/${job.id}/photos`, config);
      setPhotos(response.data?.photos || []);
    } catch (error) {
      console.error('Error loading photos:', error);
      setPhotos([]);
    }
  };

  const loadOpenItems = async () => {
    try {
      const response = await getJobOpenItems(job.id);
      setOpenItems(response.data?.open_items || []);
      setOpenItemsInProgress(response.data?.in_progress_count || 0);
    } catch (error) {
      console.error('Error loading open items:', error);
      setOpenItems([]);
      setOpenItemsInProgress(0);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await getTeamMembers();
      setTeamMembers(response.data?.team_members || response.data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    setError('');

    try {
      await addJobComment(job.id, newComment);
      setNewComment('');
      loadComments();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  // Job notes are the project's permanent record, so they aren't freely erasable.
  // The author gets a short window to pull back a typo; after that only an admin can
  // remove a note, and the removal itself stays visible. System events never go.
  const SELF_DELETE_MINUTES = 15;

  const canDelete = (comment) => {
    if (comment.is_system || comment.is_deleted) return false;
    if (isAdmin) return true;
    const isAuthor = (comment.member_id === user?.id || comment.team_member_id === user?.id);
    if (!isAuthor || !comment.created_at) return false;
    const posted = new Date(comment.created_at + (comment.created_at.includes('Z') ? '' : 'Z'));
    return (Date.now() - posted.getTime()) < SELF_DELETE_MINUTES * 60 * 1000;
  };

  const handleDeleteComment = async (comment) => {
    const msg = isAdmin && !canDeleteOutright(comment)
      ? 'Remove this note? It stays on the record as removed, showing that something was taken out and by whom.'
      : 'Delete this comment?';
    if (!window.confirm(msg)) return;

    try {
      await deleteJobComment(job.id, comment.id);
      loadComments();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete comment');
    }
  };

  // True when the note is still inside the author's own grace window, i.e. it will be
  // dropped outright rather than left as a visible removal.
  const canDeleteOutright = (comment) => {
    if (!comment.created_at) return false;
    const posted = new Date(comment.created_at + (comment.created_at.includes('Z') ? '' : 'Z'));
    const fresh = (Date.now() - posted.getTime()) < SELF_DELETE_MINUTES * 60 * 1000;
    return fresh && (comment.member_id === user?.id || comment.team_member_id === user?.id);
  };

  const handleRemoveSelf = async () => {
    if (!window.confirm('Remove yourself from this job?')) return;

    try {
      await removeSelfFromJob(job.id);
      if (onJobUpdated) onJobUpdated();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to remove from job');
    }
  };

  const handlePhotoSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhoto(true);
    setError('');

    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('photo', files[i]);
        
        const token = await getAuthToken();
        const config = {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        };
        
        await axios.post(`${API_URL}/jobs/${job.id}/photos`, formData, config);
      }
      loadPhotos();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/jobs/${job.id}/photos/${photoId}`, config);
      loadPhotos();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete photo');
    }
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    const curPos = e.target.selectionStart;
    setNewComment(value);
    setCursorPosition(curPos);

    // Check for @ mention trigger
    const textBeforeCursor = value.substring(0, curPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(atIndex + 1);
      const charBeforeAt = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
      if ((charBeforeAt === ' ' || charBeforeAt === '\n' || atIndex === 0) && !textAfterAt.includes(' ')) {
        setMentionFilter(textAfterAt.toLowerCase());
        setShowMentions(true);
        setMentionIndex(0);
        return;
      }
    }
    setShowMentions(false);
  };

  const handleKeyDown = (e) => {
    if (!showMentions) return;

    const filtered = filteredMembers;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex(prev => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filtered.length > 0) {
        e.preventDefault();
        insertMention(filtered[mentionIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  const insertMention = (member) => {
    const textBeforeCursor = newComment.substring(0, cursorPosition);
    const textAfterCursor = newComment.substring(cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    const newText = textBeforeCursor.substring(0, atIndex) + '@' + member.name + ' ' + textAfterCursor;
    setNewComment(newText);
    setShowMentions(false);
    
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = atIndex + member.name.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const filteredMembers = teamMembers.filter(member => 
    member.is_active !== false &&
    member.name.toLowerCase().includes(mentionFilter)
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + (dateStr.includes('Z') ? '' : 'Z'));
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderCommentText = (text) => {
    const parts = text.split(/(@\w+(?:\s+\w+)?)/g);
    return parts.map((part, index) => {
      if (part.startsWith('@')) {
        return (
          <span key={index} style={{
            backgroundColor: '#dbeafe',
            color: '#1d4ed8',
            padding: '1px 4px',
            borderRadius: '4px',
            fontWeight: '500'
          }}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const styles = {
    container: {
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '15px',
      marginTop: '20px',
      backgroundColor: '#fff'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px',
      paddingBottom: '10px',
      borderBottom: '1px solid #eee',
      flexWrap: 'wrap',
      gap: '10px'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      margin: 0
    },
    removeBtn: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    commentList: {
      maxHeight: '400px',
      overflowY: 'auto',
      marginBottom: '15px'
    },
    comment: {
      padding: '12px',
      marginBottom: '10px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      borderLeft: '3px solid #007bff'
    },
    // App-logged milestones — visually quieter than what people wrote, so the
    // human notes still carry the thread.
    systemComment: {
      padding: '8px 12px',
      marginBottom: '10px',
      backgroundColor: '#fbfbfc',
      borderRadius: '8px',
      borderLeft: '3px solid #cbd5e1',
      color: '#475569',
      fontSize: '13px'
    },
    deletedComment: {
      padding: '10px 12px',
      marginBottom: '10px',
      backgroundColor: '#fdf6f6',
      borderRadius: '8px',
      borderLeft: '3px dashed #d9a3a3',
      color: '#8a6060',
      fontSize: '13px',
      fontStyle: 'italic'
    },
    openPanel: {
      border: '1px solid #f0c987',
      backgroundColor: '#fffbf0',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '15px'
    },
    openPanelHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
      fontWeight: '600',
      color: '#8a5a00'
    },
    openItem: {
      padding: '8px 0',
      borderTop: '1px solid #f2e2c4'
    },
    openItemMeta: {
      fontSize: '11px',
      color: '#8a7a5a',
      marginTop: '2px'
    },
    openItemComment: {
      fontSize: '12px',
      color: '#5a4a2a',
      marginTop: '4px',
      paddingLeft: '10px',
      borderLeft: '2px solid #e5cfa3'
    },
    commentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    },
    commentAuthor: {
      fontWeight: '600',
      color: '#333'
    },
    commentDate: {
      fontSize: '12px',
      color: '#666'
    },
    commentText: {
      margin: 0,
      whiteSpace: 'pre-wrap',
      lineHeight: '1.5'
    },
    deleteBtn: {
      background: 'none',
      border: 'none',
      color: '#dc3545',
      cursor: 'pointer',
      fontSize: '12px',
      padding: '2px 6px'
    },
    form: {
      position: 'relative'
    },
    formInner: {
      display: 'flex',
      gap: '10px',
      alignItems: 'flex-end'
    },
    textarea: {
      flex: 1,
      padding: '10px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      resize: 'vertical',
      minHeight: '60px',
      fontFamily: 'inherit'
    },
    submitBtn: {
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '4px',
      cursor: 'pointer',
      height: '42px'
    },
    photoBtn: {
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      padding: '10px 14px',
      borderRadius: '4px',
      cursor: 'pointer',
      height: '42px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    noComments: {
      color: '#666',
      fontStyle: 'italic',
      textAlign: 'center',
      padding: '20px'
    },
    error: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '10px'
    },
    assignedBadge: {
      backgroundColor: '#28a745',
      color: 'white',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      whiteSpace: 'nowrap'
    },
    mentionDropdown: {
      position: 'absolute',
      bottom: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '8px',
      boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
      maxHeight: '200px',
      overflowY: 'auto',
      marginBottom: '4px',
      zIndex: 1000
    },
    mentionItem: {
      padding: '10px 14px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      borderBottom: '1px solid #f0f0f0'
    },
    mentionItemActive: {
      backgroundColor: '#e3f2fd'
    },
    mentionAvatar: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: '#2563eb',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '600',
      fontSize: '14px'
    },
    mentionHint: {
      fontSize: '12px',
      color: '#666',
      marginBottom: '8px'
    },
    photosSection: {
      marginTop: '20px',
      paddingTop: '15px',
      borderTop: '1px solid #eee'
    },
    photosSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px'
    },
    photosGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
      gap: '12px'
    },
    photoItem: {
      position: 'relative',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: '#f3f4f6'
    },
    photoImage: {
      width: '100%',
      height: '120px',
      objectFit: 'cover',
      cursor: 'pointer'
    },
    photoDeleteBtn: {
      position: 'absolute',
      top: '4px',
      right: '4px',
      backgroundColor: 'rgba(220, 38, 38, 0.9)',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    photoMeta: {
      padding: '6px 8px',
      fontSize: '11px',
      color: '#666',
      backgroundColor: '#f9fafb'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Job Comments & Photos</h3>
        {isAssigned && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={styles.assignedBadge}>✓ Assigned</span>
            <button style={styles.removeBtn} onClick={handleRemoveSelf}>
              Remove Me
            </button>
          </div>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {openItems.length > 0 && (
        <div style={styles.openPanel}>
          <div style={styles.openPanelHeader} onClick={() => setShowOpenItems(v => !v)}>
            {showOpenItems ? <FiChevronDown /> : <FiChevronRight />}
            <FiAlertCircle />
            Still open on this job ({openItems.length})
          </div>
          {showOpenItems && (
            <div>
              {openItems.map(item => (
                <div key={item.item_id} style={styles.openItem}>
                  <div style={{ fontWeight: '500', color: '#5a4a2a' }}>{item.item_text}</div>
                  <div style={styles.openItemMeta}>
                    {item.category}
                    {item.checklist_name ? ` • ${item.checklist_name}` : ''}
                    {item.visit_date ? ` • ${formatDate(item.visit_date)}` : ''}
                  </div>
                  {item.legacy_note && (
                    <div style={styles.openItemComment}>{item.legacy_note}</div>
                  )}
                  {(item.comments || []).map(c => (
                    <div key={c.id} style={styles.openItemComment}>
                      <strong>{c.author_name}:</strong> {c.text}
                    </div>
                  ))}
                </div>
              ))}
              {openItemsInProgress > 0 && (
                <div style={{ ...styles.openItemMeta, paddingTop: '8px' }}>
                  {openItemsInProgress} visit{openItemsInProgress === 1 ? '' : 's'} still in
                  progress — those items aren't counted until the visit is closed out.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div style={styles.commentList}>
        {(!comments || comments.length === 0) ? (
          <p style={styles.noComments}>No comments yet. Be the first to add one!</p>
        ) : (
          comments.map(comment => {
            if (comment.is_deleted) {
              return (
                <div key={comment.id} style={styles.deletedComment}>
                  Note removed{comment.deleted_by_name ? ` by ${comment.deleted_by_name}` : ''}
                  {comment.deleted_at ? ` on ${formatDate(comment.deleted_at)}` : ''}
                  {' '}(originally posted by {comment.member_name || 'unknown'} on {formatDate(comment.created_at)})
                </div>
              );
            }

            if (comment.is_system) {
              return (
                <div key={comment.id} style={styles.systemComment}>
                  <span>{comment.comment}</span>
                  <span style={styles.commentDate}>
                    {' '}• {comment.member_name} • {formatDate(comment.created_at)}
                  </span>
                </div>
              );
            }

            return (
              <div key={comment.id} style={styles.comment}>
                <div style={styles.commentHeader}>
                  <div>
                    <span style={styles.commentAuthor}>{comment.member_name || comment.team_member_name}</span>
                    <span style={styles.commentDate}> • {formatDate(comment.created_at)}</span>
                  </div>
                  {canDelete(comment) && (
                    <button
                      style={styles.deleteBtn}
                      onClick={() => handleDeleteComment(comment)}
                      title={canDeleteOutright(comment) ? 'Delete comment' : 'Remove note (stays on the record)'}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <p style={styles.commentText}>{renderCommentText(comment.comment)}</p>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={handleAddComment} style={styles.form}>
        <div style={styles.mentionHint}>
          💡 A good note answers: what did you <strong>find</strong>, what did you <strong>decide and why</strong>,
          and what's <strong>still open</strong>? Write it for someone opening this job cold in five years.
          Type @ to mention a team member and notify them.
        </div>
        
        {showMentions && filteredMembers.length > 0 && (
          <div style={styles.mentionDropdown}>
            {filteredMembers.slice(0, 8).map((member, index) => (
              <div
                key={member.id}
                style={{
                  ...styles.mentionItem,
                  ...(index === mentionIndex ? styles.mentionItemActive : {})
                }}
                onClick={() => insertMention(member)}
                onMouseEnter={() => setMentionIndex(index)}
              >
                <div style={styles.mentionAvatar}>
                  {member.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: '500' }}>{member.name}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>{member.role}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div style={{ ...styles.formInner, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-end' }}>
          <textarea
            ref={textareaRef}
            style={styles.textarea}
            value={newComment}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="What did you find? What did you decide, and why? What's still open?"
            disabled={loading}
          />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handlePhotoSelect}
            accept="image/*"
            multiple
            style={{ display: 'none' }}
          />
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              style={styles.photoBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              title="Add photo"
            >
              <FiCamera size={18} />
            </button>
            <button
              type="submit"
              style={{ ...styles.submitBtn, flex: isMobile ? 1 : 'none' }}
              disabled={loading || !newComment.trim()}
            >
              {loading ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      </form>

      {/* Photos Section */}
      <div style={styles.photosSection}>
        <div style={styles.photosSectionHeader}>
          <h4 style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiImage /> Photos ({photos.length})
          </h4>
          {uploadingPhoto && <span style={{ color: '#666', fontSize: '13px' }}>Uploading...</span>}
        </div>
        
        {photos.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
            No photos yet. Click the camera button to add one!
          </p>
        ) : (
          <div style={styles.photosGrid}>
            {photos.map(photo => (
              <div key={photo.id} style={styles.photoItem}>
                <img
                  src={`${API_URL}/jobs/${job.id}/photos/${photo.id}/image`}
                  alt={photo.caption || 'Job photo'}
                  style={styles.photoImage}
                  onClick={() => setLightboxPhoto(photo)}
                />
                {(photo.uploaded_by_id === user?.id || user?.role === 'Owner' || user?.role === 'Admin') && (
                  <button
                    style={styles.photoDeleteBtn}
                    onClick={() => handleDeletePhoto(photo.id)}
                    title="Delete photo"
                  >
                    <FiTrash2 size={12} />
                  </button>
                )}
                <div style={styles.photoMeta}>
                  {photo.uploaded_by_name} • {formatDate(photo.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lightboxPhoto && (() => {
        const isPhone = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || window.innerWidth < 768;
        const photoUrl = `${API_URL}/jobs/${job.id}/photos/${lightboxPhoto.id}/image`;
        const canDelete = lightboxPhoto.uploaded_by_id === user?.id || user?.role === 'Owner' || user?.role === 'Admin';
        const handleDownload = async () => {
          try {
            const config = await getAuthConfig();
            const res = await axios.get(photoUrl, { ...config, responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = lightboxPhoto.original_filename || `photo-${lightboxPhoto.id}.jpg`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
          } catch (e) {
            window.open(photoUrl, '_blank');
          }
        };
        const handleDeleteFromLightbox = async () => {
          if (!window.confirm('Delete this photo?')) return;
          try {
            const config = await getAuthConfig();
            await axios.delete(`${API_URL}/jobs/${job.id}/photos/${lightboxPhoto.id}`, config);
            setLightboxPhoto(null);
            loadPhotos();
          } catch (e) { alert('Could not delete'); }
        };
        return (
          <div
            onClick={() => setLightboxPhoto(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.95)',
              display: 'flex', flexDirection: 'column',
              justifyContent: 'center', alignItems: 'center',
              zIndex: 999999,
              padding: isPhone ? '12px' : '20px',
              paddingTop: isPhone ? 'max(12px, env(safe-area-inset-top))' : '20px',
              paddingBottom: isPhone ? 'max(12px, env(safe-area-inset-bottom))' : '20px',
            }}
          >
            <button
              onClick={() => setLightboxPhoto(null)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: isPhone ? 'max(12px, env(safe-area-inset-top))' : '20px',
                left: isPhone ? '12px' : '20px',
                width: '44px', height: '44px', borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.15)', color: 'white',
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <FiX size={24} />
            </button>

            <div style={{
              position: 'absolute',
              top: isPhone ? 'max(12px, env(safe-area-inset-top))' : '20px',
              right: isPhone ? '12px' : '20px',
              display: 'flex', gap: '8px',
            }}>
              <button
                onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                aria-label="Download"
                style={{
                  padding: isPhone ? '0' : '10px 20px',
                  width: isPhone ? '44px' : 'auto',
                  height: isPhone ? '44px' : 'auto',
                  borderRadius: isPhone ? '50%' : '6px',
                  backgroundColor: '#28a745', color: 'white', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}
              >
                <FiDownload size={isPhone ? 20 : 16} /> {!isPhone && 'Download'}
              </button>
              {canDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteFromLightbox(); }}
                  aria-label="Delete"
                  style={{
                    padding: isPhone ? '0' : '10px 20px',
                    width: isPhone ? '44px' : 'auto',
                    height: isPhone ? '44px' : 'auto',
                    borderRadius: isPhone ? '50%' : '6px',
                    backgroundColor: '#dc3545', color: 'white', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}
                >
                  <FiTrash2 size={isPhone ? 20 : 16} /> {!isPhone && 'Delete'}
                </button>
              )}
            </div>

            <img
              src={photoUrl}
              alt={lightboxPhoto.caption || 'Job photo'}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: isPhone ? '100%' : '90%',
                maxHeight: isPhone ? 'calc(100dvh - 160px)' : '80vh',
                objectFit: 'contain', borderRadius: '8px',
              }}
            />
            {lightboxPhoto.caption && (
              <div style={{ color: 'white', marginTop: '12px', textAlign: 'center', fontSize: '13px', opacity: 0.85, maxWidth: '90%' }}>
                {lightboxPhoto.caption}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default JobComments;